import { GetZenith } from '@components/hub/capsules/GetZenith';
import { useQueryClient } from '@tanstack/react-query';
import { useActivateCapsuleMutation } from '@http';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button';
import { Modal } from '@components/ui/Modal';
import * as Shared from '@shared/lib';
import { useAppStore } from '@stores';
import { useState } from 'react';

interface CapsuleModalProps {
  open: boolean;
  onClose: () => void;
};

export function CapsuleModal({ open, onClose }: CapsuleModalProps) {
  const [showInfo, setShowInfo] = useState(true);

  const { t } = useTranslation(undefined, { keyPrefix: 'Capsules' });
  const { mutateAsync } = useActivateCapsuleMutation();
  const { userData, isFetching, setIsFetching, refetch } = useAppStore();
  const queryClient = useQueryClient();

  const activateCapsule = async (id: number) => {
    if (isFetching) return;
    setIsFetching(true);

    await mutateAsync({ id });
    refetch();
    onClose();

    queryClient.refetchQueries({ queryKey: ['capsules'] });
  };

  return (
    <Modal onRequestClose={onClose} title={t('modalTitle')} isOpen={open}>
      {showInfo && (
        <div className="p-2">
          <p className="mb-2 text-sm">{t('capsuleDescription')}</p>
          <p className="mb-2 text-sm">{t('capsuleDescription2')}</p>
          <p className="mb-2 text-sm slight-active">{t('capsuleDescription3')}</p>

          <Button onClick={() => setShowInfo(false)} className="btn-active mt-2">
            {t('continue')}
          </Button>
        </div>
      )}

      {!showInfo && (
        <>
          <div className="bg-tertiary p-2 flex items-center justify-between mb-2">
            <p className="text-sm leading-none">{t('zenith')}</p>
            <div className="flex items-center">
              <p className="text-mono text-sm active leading-none">{userData.zenith}</p>
              <img src="/assets/items/zenith.avif" className="h-4 ml-1" />
            </div>
          </div>

          <div className="p-2">
            <GetZenith />

            {Shared.capsuleData.map(capsule => (
              <div className="capsule-outer" key={capsule.id}>
                <div className="capsule-container" key={capsule.id}>
                  <div className="capsule-img-container grid-overlay">
                    <img src={`/img/capsules/capsule${capsule.id}.avif`} />
                  </div>

                  <div className="ml-2 flex flex-col justify-center text-sm">
                    <p>{t(`Types.${capsule.type}.name`)}</p>
                    <p className="text-primary text-xs">{t(`Types.${capsule.type}.description`)}</p>
                  </div>
                </div>

                <button
                  className="btn btn-active border-0 border-t"
                  onClick={() => activateCapsule(capsule.id)}
                  disabled={userData.zenith < capsule.zenith}
                >
                  <div className="flex items-center justify-between">
                    <p>{t('purchase')}</p>
                    <div className="flex items-center">
                      <p className="text-sm text-mono">{capsule.zenith}</p>
                      <img src="/assets/items/zenith.avif" className="h-4.5 w-4.5 ml-1" />
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}